import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { api, fileUrl } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Storefront, Sparkle, CheckCircle, QrCode } from "@phosphor-icons/react";

// Renders 8 slots (one face) in 2 rows of 4.
export function PostcardFace({ face, slots, mode, selected = [], onSlotClick, onRedeem, onShare }) {
  const faceSlots = slots
    .filter((s) => s.face === face)
    .sort((a, b) => a.slot_number - b.slot_number);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 border-t border-l border-slate-300 bg-slate-100">
      {faceSlots.map((slot) => {
        const isReserved = slot.status === "Reserved";
        const isSelected = selected.includes(slot.id);

        if (mode === "consumer") {
          return (
            <ConsumerSlot key={slot.id} slot={slot} isReserved={isReserved} onRedeem={onRedeem} onShare={onShare} />
          );
        }
        // merchant mockup mode
        return (
          <button
            key={slot.id}
            type="button"
            disabled={isReserved}
            onClick={() => !isReserved && onSlotClick(slot)}
            data-testid={`merchant-slot-${slot.face}-${slot.slot_number}`}
            className={`slot-cell relative aspect-square border-r border-b border-slate-300 p-3 text-left flex flex-col justify-between
              ${isReserved ? "bg-amber-50 cursor-not-allowed border-l-2 border-l-amber-500" : "bg-white hover:bg-emerald-50 active:scale-95"}
              ${isSelected ? "ring-2 ring-inset ring-emerald-600 bg-emerald-50 z-10" : ""}`}
          >
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
              {slot.face} · {slot.slot_number}
            </span>
            {isReserved ? (
              <div className="flex flex-col items-start">
                <span className="text-xs font-[700] text-amber-700">Reserved</span>
                <span className="text-[11px] text-amber-800/80 line-clamp-1">{slot.business_name}</span>
              </div>
            ) : (
              <div>
                {isSelected ? (
                  <CheckCircle size={22} weight="fill" className="text-emerald-600" />
                ) : (
                  <span className="text-[11px] text-emerald-700 font-[600]">{mode === "sponsor" ? "Claim Spot →" : "Available"}</span>
                )}
                <div className="font-display text-lg text-navy-900">${slot.price}</div>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

function ConsumerSlot({ slot, isReserved, onRedeem, onShare }) {
  const [qrOpen, setQrOpen] = useState(false);
  const [qrToken, setQrToken] = useState(null);
  const validateUrl = `${window.location.origin}/validate/${slot.id}`;
  const qrValue = qrToken ? `${validateUrl}?t=${qrToken}` : validateUrl;
  const cap = slot.redemption_cap || 0;
  const closed = cap > 0 && (slot.total_redemptions || 0) >= cap;
  const remaining = cap > 0 ? Math.max(0, cap - (slot.total_redemptions || 0)) : null;
  useEffect(() => {
    if (qrOpen && isReserved) api.get(`/slots/${slot.id}/redeem-token`).then(({ data }) => setQrToken(data.token)).catch(() => {});
    if (!qrOpen) setQrToken(null);
    // eslint-disable-next-line
  }, [qrOpen]);
  if (!isReserved) {
    return (
      <div className="aspect-square border-r border-b border-slate-300 bg-slate-50 flex items-center justify-center">
        <span className="text-[10px] uppercase tracking-widest text-slate-300 font-bold rotate-[-8deg]">Open Slot</span>
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="aspect-square border-r border-b border-slate-300 bg-white p-4 flex flex-col justify-between group"
      data-testid={`consumer-slot-${slot.id}`}
    >
      <div>
        <div className="flex items-center gap-2 mb-1">
          {slot.logo_file_id ? (
            <img src={fileUrl(slot.logo_file_id)} alt="" className="w-8 h-8 object-cover rounded-sm border border-slate-200" />
          ) : (
            <span className="w-8 h-8 rounded-sm bg-navy-900 text-white flex items-center justify-center">
              <Storefront size={16} weight="duotone" />
            </span>
          )}
          <span className="text-xs font-[700] text-navy-900 line-clamp-2 leading-tight">{slot.business_name}</span>
        </div>
        {slot.coupon_file_id && (
          <img src={fileUrl(slot.coupon_file_id)} alt="" className="w-full h-14 object-cover rounded-sm my-1 border border-slate-100" />
        )}
        <p className="text-[13px] text-emerald-800 font-[600] leading-snug mt-1 line-clamp-2">{slot.offer_details}</p>
        {remaining !== null && (
          <span data-testid={`remaining-${slot.id}`} className={`inline-block mt-1 text-[10px] font-bold uppercase tracking-wider ${closed ? "text-red-600" : "text-amber-700"}`}>
            {closed ? "Limit reached" : `${remaining} left`}
          </span>
        )}
      </div>
      <div className="flex flex-col gap-1.5 mt-2">
        <button
          onClick={() => onRedeem(slot)}
          disabled={closed}
          data-testid={`redeem-btn-${slot.id}`}
          className={`w-full text-xs font-[600] py-2 transition-colors duration-200 ${closed ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-navy-900 text-white hover:bg-emerald-700"}`}
        >
          {closed ? "Offer Closed" : "Redeem Offer"}
        </button>
        <div className="flex gap-1.5">
          <button
            onClick={() => onShare(slot)}
            data-testid={`share-btn-${slot.id}`}
            className="flex-1 border border-slate-300 text-slate-700 text-xs font-[600] py-1.5 hover:border-amber-600 hover:text-amber-700 transition-colors duration-200 flex items-center justify-center gap-1"
          >
            <Sparkle size={12} weight="duotone" /> Share
          </button>
          <button
            onClick={() => setQrOpen(true)}
            data-testid={`qr-btn-${slot.id}`}
            aria-label="Show redemption QR code"
            className="border border-slate-300 text-navy-900 text-xs font-[600] px-2.5 py-1.5 hover:border-emerald-600 hover:text-emerald-700 transition-colors duration-200 flex items-center justify-center"
          >
            <QrCode size={16} weight="duotone" />
          </button>
        </div>
      </div>
      <Dialog open={qrOpen} onOpenChange={setQrOpen}>
        <DialogContent className="sm:max-w-sm" data-testid={`qr-dialog-${slot.id}`}>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight text-left">{slot.business_name}</DialogTitle>
            <DialogDescription className="text-left">Scan this code in-store to validate your redemption.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center text-center">
            <p className="text-sm text-emerald-800 font-[600] mb-4">{slot.offer_details}</p>
            <div className="p-4 bg-white border border-slate-200">
              <QRCodeSVG value={qrValue} size={200} fgColor="#0b1a33" level="M" />
            </div>
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
              Show this to the merchant. They scan to validate your redemption in-store.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
